import React from 'react';
import { S6FAQ, FAQItem } from '@milanlaser/mlcomponents';
import { processBackgroundColor } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';
import type { Store } from '../../../../clinics/clinics';
import type { Metro } from '../../../../clinics/clinics';
import { LocationBuilderState, getDynamicLocationFAQs } from '../../../../utils/general/builder';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state?: BuilderState<T>;
    };
};

type S6FAQArgs = React.ComponentProps<typeof S6FAQ> &
    BuilderProps<{
        model: string;
        store?: Store;
        metro?: Metro;
    }> & {
        desktopPaddingTop?: PaddingSize;
        desktopPaddingBottom?: PaddingSize;
        mobilePaddingTop?: PaddingSize;
        mobilePaddingBottom?: PaddingSize;
        faqs: FAQItem[];
    };

function resolveFaqs(
    faqs: FAQItem[],
    replaceVariables: (s: string) => string,
    isStatePage: boolean,
    isMetroPage: boolean,
    isClinicPage: boolean,
    metroFaqs: FAQItem[] | undefined,
    clinicFaqsFromCSA: FAQItem[] | undefined,
    locationName: string | null
): FAQItem[] {
    const processedFaqs = faqs.map((faq) => ({ ...faq, answer: replaceVariables(faq.answer) }));
    const defaultLocationFaqs = locationName ? getDynamicLocationFAQs(locationName) : [];

    if (isMetroPage && metroFaqs?.length) {
        return [...defaultLocationFaqs, ...processedFaqs, ...metroFaqs];
    } else if (isClinicPage && clinicFaqsFromCSA?.length) {
        return [...defaultLocationFaqs, ...processedFaqs, ...clinicFaqsFromCSA];
    } else if (isStatePage || isMetroPage || isClinicPage) {
        return [...defaultLocationFaqs, ...processedFaqs];
    } else {
        return processedFaqs;
    }
}

export function S6FAQSection(props: S6FAQArgs) {
    const { replaceVariables } = useFirebaseMetrics();
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        faqs = [],
        headline,
        ...restProps
    } = props;

    const { builderState } = restProps;

    const isStatePage = builderState.state?.model === 'state-page';
    const isMetroPage = builderState.state?.model === 'metro-page';
    const isClinicPage = builderState.state?.model === 'clinic-page';
    const locationName = getLocationContext(builderState as LocationBuilderState);

    const mergedFaqs = resolveFaqs(
        faqs,
        replaceVariables,
        isStatePage,
        isMetroPage,
        isClinicPage,
        isMetroPage ? builderState.state?.metro?.metro_faq : undefined,
        builderState?.state?.store?.clinic_faqs,
        locationName
    );

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const resolvedHeadline =
        (isStatePage || isMetroPage || isClinicPage) && locationName
            ? `Laser Hair Removal ${locationName} FAQs`
            : headline;

    const modifiedProps = {
        ...restProps,
        headline: resolvedHeadline,
        faqItems: mergedFaqs,
        backgroundGradient: processBackgroundColor({
            backgroundGradient: props.backgroundGradient,
        }),
        backgroundColor: processBackgroundColor({
            backgroundColor: props.backgroundColor,
        }),
        className: `${topPaddingClasses} ${bottomPaddingClasses} mcl-px-4 md:mcl-px-6 xl:mcl-px-4`,
    };

    return <S6FAQ {...modifiedProps} />;
}
